import React, { useState, useCallback, useEffect, useRef } from 'react';
import './SliderGallery.css';
import { makeImageKitURL, EImageKitTransform, IMakeImageKitURL } from '../../lib/Media/util';
import { useImageKitConfig } from '../Media/ImageKitProvider';

export interface SliderImage {
  id: number;
  src: string;
  thumbnail: string;
  alt: string;
  caption?: string;
}

export interface SliderGalleryProps {
  images: SliderImage[];
  autoPlay?: boolean;
  autoPlayInterval?: number;
  showNav?: boolean;
  showThumbnails?: boolean;
  showCaptions?: boolean;
  showFullscreen?: boolean;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
  onImageChange?: (image: SliderImage, index: number) => void;
  // ImageKit optimization props
  imageKitOptions?: {
    preset?: EImageKitTransform;
    quality?: number;
    width?: number;
    height?: number;
    background?: string;
    output?: 'jpg' | 'webp' | 'avif' | 'png';
    urlEndpoint?: string;
  };
  // Performance props
  preloadCount?: number;
  eagerLoadCount?: number; // Number of images to load with high priority
}

export const SliderGallery: React.FC<SliderGalleryProps> = ({
  images,
  autoPlay = false,
  autoPlayInterval = 3000,
  showNav = true,
  showThumbnails = true,
  showCaptions = false,
  showFullscreen = false,
  height,
  className = '',
  style = {},
  onImageChange,
  imageKitOptions = {
    preset: EImageKitTransform.cover3x2,
    quality: 85,
    width: 1920,
    height: 1080,
    background: 'FFFFFF',
    output: 'webp',
    urlEndpoint: 'https://ik.imagekit.io/ggmtouwe2'
  },
  preloadCount = 2,
  eagerLoadCount = 5 // Default to 5 for eager loading
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());
  const [currentImageLoaded, setCurrentImageLoaded] = useState(false);
  const imageRefs = useRef<{ [key: number]: any }>({});
  const mainImageRef = useRef<HTMLImageElement>(null);
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true); // State to track if it's the initial eager load
  const thumbnailsRef = useRef<HTMLDivElement>(null);

  // ImageKit configuration (optional - only used if useImageKit is true)
  let imageKitConfig = null;
  try {
    imageKitConfig = useImageKitConfig();
  } catch (error) {
    // ImageKit provider not found, will fall back to direct options or WSRV
  }

  // Function to scroll active thumbnail into view
  const scrollActiveIntoView = useCallback(() => {
    if (!thumbnailsRef.current) return;
    
    const thumbnailsContainer = thumbnailsRef.current;
    const activeThumbnail = thumbnailsContainer.querySelector('.slider-thumbnail.active') as HTMLElement;
    
    if (activeThumbnail) {
      // Get the thumbnail position relative to the container
      const thumbnailLeft = activeThumbnail.offsetLeft;
      const thumbnailWidth = activeThumbnail.offsetWidth;
      const containerWidth = thumbnailsContainer.offsetWidth;
      const currentScrollLeft = thumbnailsContainer.scrollLeft;
      
      // Calculate if the thumbnail is visible
      const thumbnailStart = thumbnailLeft;
      const thumbnailEnd = thumbnailLeft + thumbnailWidth;
      const visibleStart = currentScrollLeft;
      const visibleEnd = currentScrollLeft + containerWidth;
      
      // If thumbnail is not fully visible, scroll to center it
      if (thumbnailStart < visibleStart || thumbnailEnd > visibleEnd) {
        const scrollTo = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2);
        thumbnailsContainer.scrollTo({
          left: Math.max(0, scrollTo), // Ensure we don't scroll past the beginning
          behavior: 'smooth'
        });
      }
    }
  }, []);

  // Utility function to optimize image URLs with ImageKit
  const getOptimizedImageUrl = useCallback((imageUrl: string, isThumbnail = false, width = 1920, height = 1080, quality = 85) => {
    // Use ImageKit for image optimization
    const effectiveConfig = imageKitConfig || {};
    const {
      preset = isThumbnail ? EImageKitTransform.thumb1x1 : EImageKitTransform.cover3x2,
      quality: ikQuality = isThumbnail ? 70 : quality,
      background = 'FFFFFF',
      output = 'webp',
      urlEndpoint
    } = { ...imageKitOptions, ...effectiveConfig };

    const finalUrlEndpoint = urlEndpoint || imageKitConfig?.urlEndpoint || 'https://ik.imagekit.io/ggmtouwe2';

    const imageKitParams: IMakeImageKitURL = {
      originURL: imageUrl,
      preset: preset,
      quality: ikQuality,
      width: width,
      height: height,
      background: background,
      output: output,
      urlEndpoint: finalUrlEndpoint
    };

    const generatedUrl = makeImageKitURL(imageKitParams);
    
    // Debug logging
    console.log('🔍 ImageKit URL Generated:');
    console.log('- Input URL:', imageUrl);
    console.log('- Generated URL:', generatedUrl);
    console.log('- Parameters:', imageKitParams);
    
    return generatedUrl;
  }, [imageKitOptions, imageKitConfig]);

  // Eager loading of initial images on mount
  // This ensures the first few images are loaded immediately, preventing loaders
  // for the initial images in production with 50+ images
  useEffect(() => {
    if (images.length === 0) return;

    const preloadImage = (image: SliderImage, priority: 'high' | 'normal' = 'normal') => {
      if (loadedImages.has(image.id)) return;
      
      const img = new Image();
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, image.id]));
        // If this is the current image, update loading state
        if (image.id === images[currentIndex]?.id) {
          setCurrentImageLoaded(true);
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
        }
      };
      img.onerror = () => {
        // Still add to loaded set to prevent infinite retries
        setLoadedImages(prev => new Set([...prev, image.id]));
        if (image.id === images[currentIndex]?.id) {
          setCurrentImageLoaded(true);
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
        }
      };
      
      // Use different quality settings based on priority
      const quality = priority === 'high' ? 90 : 70;
      img.src = getOptimizedImageUrl(image.src, false, 1280, 696, quality);
    };

    // Eagerly load the first few images with high priority
    for (let i = 0; i < Math.min(eagerLoadCount, images.length); i++) {
      const eagerImage = images[i];
      if (eagerImage) {
        preloadImage(eagerImage, 'high');
      }
    }
  }, [images, eagerLoadCount, getOptimizedImageUrl, currentIndex, isInitialLoad]); // Only run on mount and when images change

  // Smart preloading based on current position
  useEffect(() => {
    const preloadImage = (image: SliderImage) => {
      if (loadedImages.has(image.id)) return;
      
      const img = new Image();
      img.onload = () => {
        setLoadedImages(prev => new Set([...prev, image.id]));
        // If this is the current image, update loading state
        if (image.id === images[currentIndex]?.id) {
          setCurrentImageLoaded(true);
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
        }
      };
      img.onerror = () => {
        // Still add to loaded set to prevent infinite retries
        setLoadedImages(prev => new Set([...prev, image.id]));
        if (image.id === images[currentIndex]?.id) {
          setCurrentImageLoaded(true);
          if (isInitialLoad) {
            setIsInitialLoad(false);
          }
        }
      };
      img.src = getOptimizedImageUrl(image.src);
    };

    // Preload current image if not already loaded
    const currentImage = images[currentIndex];
    if (currentImage && !loadedImages.has(currentImage.id)) {
      preloadImage(currentImage);
    }

    // Smart preloading: prioritize next images, then previous
    const preloadNext = () => {
      for (let i = 1; i <= preloadCount; i++) {
        const nextIndex = (currentIndex + i) % images.length;
        const nextImage = images[nextIndex];
        if (nextImage && !loadedImages.has(nextImage.id)) {
          preloadImage(nextImage);
        }
      }
    };

    const preloadPrevious = () => {
      for (let i = 1; i <= preloadCount; i++) {
        const prevIndex = currentIndex - i < 0 ? images.length + (currentIndex - i) : currentIndex - i;
        const prevImage = images[prevIndex];
        if (prevImage && !loadedImages.has(prevImage.id)) {
          preloadImage(prevImage);
        }
      }
    };

    // Prioritize next images (user is more likely to go forward)
    preloadNext();
    
    // Then preload previous images
    setTimeout(() => {
      preloadPrevious();
    }, 100); // Small delay to prioritize next images
  }, [currentIndex, images, loadedImages, preloadCount, getOptimizedImageUrl, isInitialLoad]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || images.length <= 1) return;

    const interval = setInterval(() => {
      setSlideDirection('right');
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, autoPlayInterval, images.length]);

  // Scroll active thumbnail into view when currentIndex changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      scrollActiveIntoView();
    }, 100); // Small delay to ensure DOM is updated

    return () => clearTimeout(timeoutId);
  }, [currentIndex, scrollActiveIntoView]);

  // Scroll to active thumbnail when fullscreen mode is toggled
  useEffect(() => {
    if (isFullscreen) {
      const timeoutId = setTimeout(() => {
        // First scroll to the beginning, then to the active thumbnail
        if (thumbnailsRef.current) {
          thumbnailsRef.current.scrollLeft = 0;
        }
        
        setTimeout(() => {
          scrollActiveIntoView();
        }, 100);
      }, 100); // Reduced delay for better responsiveness

      return () => clearTimeout(timeoutId);
    }
  }, [isFullscreen, scrollActiveIntoView]);

  // Reset to first image on component mount
  useEffect(() => {
    if (images.length > 0) {
      setCurrentIndex(0);
      setCurrentImageLoaded(false);
      
      // Reset thumbnail scroll position
      if (thumbnailsRef.current) {
        thumbnailsRef.current.scrollLeft = 0;
      }
    }
  }, [images.length]);

  // Pause auto-play on user interaction
  const pauseAutoPlay = useCallback(() => {
    if (autoPlay) {
      setIsAutoPlaying(false);
    }
  }, [autoPlay]);

  // Resume auto-play after user interaction
  const resumeAutoPlay = useCallback(() => {
    if (autoPlay) {
      setIsAutoPlaying(true);
    }
  }, [autoPlay]);

  const goToImage = useCallback((index: number, direction: 'left' | 'right' = 'right') => {
    if (index >= 0 && index < images.length && index !== currentIndex) {
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      
      setSlideDirection(direction);
      setIsTransitioning(true);
      setCurrentImageLoaded(false);
      setCurrentIndex(index);
      onImageChange?.(images[index], index);
      pauseAutoPlay();
      
      // Reset transition state after animation
      setTimeout(() => {
        setIsTransitioning(false);
        setSlideDirection(null);
      }, 300);
      
      // Fallback timeout to prevent infinite loading state
      loadingTimeoutRef.current = setTimeout(() => {
        setCurrentImageLoaded(true);
      }, 10000); // 10 second timeout
    }
  }, [images, onImageChange, pauseAutoPlay, currentIndex, images.length]);

  const goToPrevious = useCallback(() => {
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    goToImage(newIndex, 'left');
  }, [currentIndex, images.length, goToImage]);

  const goToNext = useCallback(() => {
    const newIndex = currentIndex === images.length - 1 ? 0 : currentIndex + 1;
    goToImage(newIndex, 'right');
  }, [currentIndex, images.length, goToImage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToNext();
        break;
      case 'Home':
        e.preventDefault();
        goToImage(0, currentIndex > 0 ? 'left' : 'right');
        break;
      case 'End':
        e.preventDefault();
        goToImage(images.length - 1, currentIndex < images.length - 1 ? 'right' : 'left');
        break;
      case 'Escape':
        if (isFullscreen) {
          setIsFullscreen(false);
        }
        break;
      case 'f':
      case 'F':
        if (showFullscreen) {
          setIsFullscreen(!isFullscreen);
        }
        break;
    }
  }, [goToPrevious, goToNext, isFullscreen, showFullscreen, goToImage, currentIndex, images.length]);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsElement = document.fullscreenElement || (document as any).webkitFullscreenElement;
      setIsFullscreen(!!fsElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const enterFullscreen = useCallback(() => {
    const el = document.querySelector('.slider-gallery') as HTMLElement;
    if (el) {
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if ((el as any).webkitRequestFullscreen) {
        (el as any).webkitRequestFullscreen();
      }
    }
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      enterFullscreen();
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  const toggleAutoPlay = useCallback(() => {
    setIsAutoPlaying(!isAutoPlaying);
  }, [isAutoPlaying]);

  if (images.length === 0) {
    return (
      <div className={`slider-gallery-empty ${className}`} style={style}>
        <p>No images to display</p>
      </div>
    );
  }

  const currentImage = images[currentIndex];

  const handleImageLoad = useCallback(() => {
    setCurrentImageLoaded(true);
    // Clear the fallback timeout since image loaded successfully
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const currentSrc = img.src;
    
    // Log detailed error information for debugging
    if (currentSrc.includes('ik.imagekit.io')) {
      console.error('🔴 ImageKit Error Details:');
      console.error('- Failed URL:', currentSrc);
      console.error('- Original Image:', currentImage.src);
      console.error('- Network Status:', navigator.onLine ? 'Online' : 'Offline');
      
      // Try to fetch the URL to get more error details
      fetch(currentSrc)
        .then(response => {
          console.error('- HTTP Status:', response.status);
          console.error('- Status Text:', response.statusText);
          console.error('- Headers:', Object.fromEntries(response.headers.entries()));
          return response.text();
        })
        .then(text => {
          console.error('- Response Body:', text.substring(0, 500));
        })
        .catch(fetchError => {
          console.error('- Fetch Error:', fetchError.message);
        });
    }
    
    console.warn(`Failed to load image: ${currentImage.src}`);
    setCurrentImageLoaded(true); // Still mark as loaded to prevent infinite loading state
    
    // Clear the fallback timeout since we're handling the error
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, [currentImage.src]);

  const handleThumbnailError = useCallback((e: React.SyntheticEvent<HTMLImageElement>, originalSrc: string) => {
    const img = e.currentTarget;
    const currentSrc = img.src;
    
    // Log detailed error information for debugging thumbnails
    if (currentSrc.includes('ik.imagekit.io')) {
      console.error('🔴 ImageKit Thumbnail Error:', currentSrc);
      console.error('- Original:', originalSrc);
    }
    
    console.warn(`Failed to load thumbnail: ${originalSrc}`);
  }, []);

  // Prevent context menu and download
  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    return false;
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    return false;
  }, []);

  // Show loading spinner only if image is not loaded, we're not in initial load phase, and not transitioning
  const showLoadingSpinner = !currentImageLoaded && !isInitialLoad && !isTransitioning;

  return (
    <div 
      className={`slider-gallery ${isFullscreen ? 'fullscreen' : ''} ${className}`}
      style={{
        width: isFullscreen ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : height,
        ...style
      }}
      onKeyDown={handleKeyDown}
      onContextMenu={handleContextMenu}
      tabIndex={0}
    >
      {/* Main Image Container */}
      <div className="slider-main-container" style={{aspectRatio: isFullscreen ? 'unset' : '1.84/1'}}>
        <div className="slider-image-container">
          <img
            ref={mainImageRef}
            src={getOptimizedImageUrl(currentImage.src, false, 1280, 696)}
            alt={currentImage.alt}
            loading="eager"
            className={`slider-main-image ${isTransitioning ? `slide-${slideDirection}` : ''} ${showLoadingSpinner ? 'loading' : ''}`}
            {...{ fetchpriority: "high" }}
            onLoad={handleImageLoad}
            onError={handleImageError}
            onMouseEnter={pauseAutoPlay}
            onMouseLeave={resumeAutoPlay}
            onContextMenu={handleContextMenu}            
            onDragStart={handleDragStart}
            style={{
              width: '100%',
              height: isFullscreen ? 'calc(100vh - 120px)' : '100%', // Leave space for thumbnails in fullscreen
              objectFit: 'cover',
              objectPosition: 'center',
            }}
          />
          
          {/* Loading Spinner */}
          {showLoadingSpinner && (
            <div className="slider-loading-spinner">
              <div className="spinner"></div>
            </div>
          )}
          
          {/* Caption */}
          {showCaptions && currentImage.caption && (
            <div className="slider-caption">
              <p>{currentImage.caption}</p>
            </div>
          )}
        </div>

        {/* Navigation Controls */}
        {showNav && images.length > 1 && (
          <>
            <button
              className="slider-nav slider-nav-prev"
              onClick={goToPrevious}
              onMouseEnter={pauseAutoPlay}
              onMouseLeave={resumeAutoPlay}
              aria-label="Previous image"
              disabled={isTransitioning}
            >
              ‹
            </button>
            <button
              className="slider-nav slider-nav-next"
              onClick={goToNext}
              onMouseEnter={pauseAutoPlay}
              onMouseLeave={resumeAutoPlay}
              aria-label="Next image"
              disabled={isTransitioning}
            >
              ›
            </button>
          </>
        )}

        {/* Control Buttons */}
        <div className="slider-controls">
          {showFullscreen && (
            <button
              className="slider-control-btn"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              {isFullscreen ? '✕' : '⤢'}
            </button>
          )}
          
          {autoPlay && (
            <button
              className="slider-control-btn"
              onClick={toggleAutoPlay}
              aria-label={isAutoPlaying ? 'Pause slideshow' : 'Play slideshow'}
            >
              {isAutoPlaying ? '⏸' : '▶'}
            </button>
          )}
        </div>

        {/* Image Counter */}
        <div className="slider-counter">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnails - Always show when enabled, including in fullscreen */}
      {showThumbnails && images.length > 1 && (
        <div className={`slider-thumbnails ${isFullscreen ? 'fullscreen-thumbnails' : ''}`} ref={thumbnailsRef}>
          {images.map((image, index) => (
            <div
              key={image.id}
              className={`slider-thumbnail ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToImage(index, index > currentIndex ? 'right' : 'left')}
              onMouseEnter={pauseAutoPlay}
              onMouseLeave={resumeAutoPlay}
            >
              <img
                src={getOptimizedImageUrl(image.src, true, 76, 56)}
                alt={image.alt}
                loading="eager"
                className="slider-thumbnail-image"
                {...{ fetchpriority: "high" }}
                onContextMenu={handleContextMenu}
                onDragStart={handleDragStart}
                onError={(e) => handleThumbnailError(e, image.src)}
              />
              {showCaptions && image.caption && (
                <div className="slider-thumbnail-caption">
                  <p>{image.caption}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}; 